/**
 * The host shell Kola runs in (2026-09, docs/06 §2.4 addendum).
 *
 * Kola's client has two shells now: the WEB shell — the SPA the Kola server
 * serves, used by browsers and the mobile WebView — and the EMBEDDED shell,
 * mounted as a native page inside the Rainvic Tool desktop app. Everything
 * that differs between them goes through this one module:
 *
 *  - where the server is (`apiBase`): the web shell is same-origin and keeps
 *    its relative `/api` + `/collab` paths; the embedded shell names the
 *    origin, because the desktop renderer is a loopback http origin;
 *  - how a request proves who it is (`getIdToken`): the web shell rides the
 *    same-origin kola_session cookie; the embedded shell sends the Firebase
 *    ID token itself (Bearer on REST, provider token on the socket, and a
 *    header the desktop's main process injects on asset loads) because a
 *    SameSite cookie never leaves the Kola origin;
 *  - how a link leaves Kola (`openExternal`): `window.open` in a browser,
 *    the OS default browser through the desktop's shell hand-off;
 *  - the theme the embedded shell starts on (its host's); the reader's own
 *    toggle still wins;
 *  - who else hears that a board's name, stage or dates moved
 *    (`boardsChanged`): nobody in a browser tab; the desktop app's other
 *    windows, which show the same boards from their own fetches;
 *  - which views the dashboard offers besides Board (`dashboardViews`): none
 *    in a browser; the desktop app's Project and Calendar views, drawn from
 *    data only it has (its Firestore overlay and review statuses);
 *  - where a Short gets released (`openReleaseInReview`, F-152): nowhere a
 *    browser or a phone can reach; the desktop app's Review page, whose
 *    YouTube upload marks the Short released when YouTube has the video;
 *  - who must know the page holds unsaved work (`setUnsavedWork`): nobody
 *    in a browser, where `beforeunload` alone decides; the desktop app,
 *    which decides a window's unload itself;
 *  - where else a file to fill a new board can come from (`fileSource`):
 *    nowhere but the file dialog in a browser; the desktop app's Local Files
 *    — which can also move a Short's finished video beside its script (F-155).
 *
 * Feature code never asks "am I embedded?" — it calls `apiUrl`, `wsUrl`,
 * `openExternalUrl`, `setDocumentTitle` and gets the right answer for the
 * shell it is in. `getHost()` itself is read only by these helpers and by
 * the shell entry points (main.tsx, embedded.tsx).
 */
import type { ReactNode } from "react";

import type { Theme } from "./theme.js";

export interface KolaHost {
  readonly kind: "embedded";
  /** Origin of the Kola server, no path, no trailing slash — "https://kola-….run.app". */
  readonly apiBase: string;
  /**
   * The signed-in user's Firebase ID token, or null when nobody is signed in.
   * `forceFresh` bypasses the SDK's cache — the one retry after a 401.
   */
  getIdToken(forceFresh?: boolean): Promise<string | null>;
  /** Open an http(s) URL outside Kola (the OS default browser). */
  openExternal(url: string): void;
  /**
   * The host's own theme — what the embedded shell shows until the reader
   * picks one with the sun/moon toggle (core/theme.ts `setDefaultTheme`).
   * The choice is the reader's, remembered in localStorage like the web shell's.
   */
  readonly theme: Theme;
  /** The host application's own version, for the badge and debug reports. */
  readonly hostVersion?: string;
  /** Let the user look for an update when the server refuses this client as outdated. */
  checkForUpdates?(): void;
  /**
   * F-138: the storyboard sync folder — a host that can write files to a
   * folder the user picked. Absent on a host that cannot (the web shell has
   * no host at all; a future mobile host may have no folder), and the
   * feature's menu entries do not appear without it.
   */
  readonly storyboardSync?: StoryboardSyncHost;
  /**
   * Board metadata — name, stage, dates, archived — lives outside a board's
   * Yjs doc, so REST is the only way to see it move and nothing pushes it.
   * A host that shows the same boards elsewhere (the desktop app's other
   * windows) passes the word along: `announce` after this client changed
   * one, `subscribe` to hear that someone else did. Absent → nobody to tell.
   */
  readonly boardsChanged?: BoardsChangedHost;
  /**
   * Views the HOST draws on the dashboard, offered beside Board in its view
   * switch (2026-10: the desktop app's Project and Calendar views, which merged
   * its old Projects page into this one). Absent → Board alone.
   */
  readonly dashboardViews?: readonly HostDashboardView[];
  /**
   * F-152: take the user to the host's Review page to release a Short. A
   * Short goes out when its final cut — a review video linked to the board —
   * is uploaded to YouTube from there, and that upload is what marks the
   * slot `released` and records its link (PATCH /boards/:id). The host opens
   * the board's linked video when there is one, else Review itself so the
   * video can be linked. Absent (the web shell, the mobile WebView, an older
   * desktop) → the rail only says where releasing happens.
   */
  openReleaseInReview?(boardId: string, slot: number): void;
  /**
   * The page holds work a close or reload would lose (`key` names it — the
   * Storyboard Lab's draft not saved on this device yet): true while it
   * refuses `beforeunload`, false once it stops. The desktop app decides a
   * window's unload itself (its YouTube upload asks before closing one), and
   * must never let the page go while this still holds it. Absent → nobody
   * to tell.
   */
  setUnsavedWork?(key: string, unsaved: boolean): void;
  /**
   * F-153: files the HOST can read from folders its user already has — the
   * desktop app's Local Files (the team's shared Google Drive folders). The
   * create forms' *Fill from a file* offers them beside the OS file dialog.
   * Absent → only the file dialog.
   */
  readonly fileSource?: HostFileSource;
}

/* ------------------------------------------------------------------ */
/* F-153 host file source                                              */
/* ------------------------------------------------------------------ */

/**
 * What *Fill from a file* asks of a host that keeps its own folders. Paths are
 * the HOST's and opaque here — Kola only hands back what `folders`, `list` and
 * `search` gave it, and the host refuses anything outside its folders. Every
 * method resolves rather than throws, reporting failure in `error`.
 */
export interface HostFileSource {
  /** What the host calls the place — the button reads "From <label>…". */
  readonly label: string;
  /** The top-level folders readable on this machine. */
  folders(): Promise<HostFileFoldersResult>;
  /** One folder's subfolders and files. */
  list(dirPath: string): Promise<HostFileListResult>;
  /** Files and folders under the top-level folders whose names match `query`. */
  search?(query: string): Promise<HostFileListResult>;
  /** A file's contents as text (UTF-8). */
  readText(filePath: string): Promise<HostFileReadResult>;
  /**
   * F-155: let the user pick a video and MOVE it (nothing is uploaded) into
   * the folder of `scriptPath` — a Short's script, from its board's first
   * row — renamed to the script's name with the video's own extension.
   * Absent → a Short's *Place video* button says where it works.
   */
  placeVideo?(scriptPath: string): Promise<HostPlaceVideoResult>;
}

export interface HostPlaceVideoResult {
  /** Where the video now is. */
  path?: string;
  /** The user closed the picker (or declined to replace) — nothing moved. */
  cancelled?: boolean;
  error?: string;
}

export interface HostFileFolder {
  readonly name: string;
  readonly path: string;
  readonly badge?: HostFileBadge;
}

/**
 * A word the host attaches to a file or folder — the desktop's team status
 * ("Selected", "Dumped") on a file, how many files below a folder nobody has
 * checked yet. Kola only draws it.
 */
export interface HostFileBadge {
  readonly label: string;
  readonly tone: "positive" | "negative" | "neutral";
  /** Hover text, when the label alone is terse (a bare count). */
  readonly title?: string;
}

export interface HostFileFoldersResult {
  folders: readonly HostFileFolder[];
  /** Folders the host knows of that are not set up on this machine yet. */
  notSetUp?: number;
  error?: string;
}

export interface HostFileEntry {
  readonly name: string;
  readonly path: string;
  readonly kind: "dir" | "file";
  /** Bytes; 0 for folders. */
  readonly size: number;
  readonly mtimeMs: number;
  readonly badge?: HostFileBadge;
  /**
   * The team chose this file (the desktop's "Selected" status). A Short's
   * create form offers only these; unset reads as not chosen.
   */
  readonly selected?: boolean;
}

export interface HostFileListResult {
  entries?: readonly HostFileEntry[];
  error?: string;
}

export interface HostFileReadResult {
  text?: string;
  error?: string;
}

/** One host-drawn dashboard view. */
export interface HostDashboardView {
  /** Stable id, remembered as the reader's view; never "board". */
  readonly id: string;
  /** The view switch's label. */
  readonly label: string;
  render(context: HostDashboardViewContext): ReactNode;
}

/** What the dashboard hands a host view. */
export interface HostDashboardViewContext {
  /** The dashboard's kind filter (F-140) — the host view filters the same way. */
  readonly kindFilter: "all" | "long" | "shorts";
  /** Switch the dashboard to another view: "board" or a host view's id. */
  switchView(id: string): void;
  /** Open a board, with optional query params (`panel`, share-link params). */
  openBoard(boardId: string, params?: Record<string, string>): void;
}

export interface BoardsChangedHost {
  /** This client just changed a board's name, stage, dates or archived state. */
  announce(): void;
  /** Run `listener` when ANOTHER window did; returns the unsubscribe. */
  subscribe(listener: () => void): () => void;
}

/* ------------------------------------------------------------------ */
/* F-138 storyboard sync                                               */
/* ------------------------------------------------------------------ */

/**
 * What the sync feature asks of its host. Paths are the HOST's (absolute,
 * in its OS's form); `boardFolder` is one safe path segment under
 * `rootDir`; `file` is a relative path inside that folder (`storyboard.md`,
 * `assets/<id>.png`). Every method resolves rather than throws, reporting
 * failure in `error` — the pattern of every desktop IPC handler.
 */
export interface StoryboardSyncHost {
  /** Ask the user for the ROOT folder; null when they cancelled. */
  pickFolder(): Promise<string | null>;
  /**
   * Make sure `<rootDir>/<boardFolder>/assets` exists and report the asset
   * files already there, assetId → relative file path — what need not be
   * downloaded again.
   */
  prepare(request: { rootDir: string; boardFolder: string }): Promise<StoryboardSyncPrepareResult>;
  /** Write one asset's bytes (base64) to `file`. */
  writeAsset(request: {
    rootDir: string;
    boardFolder: string;
    file: string;
    base64: string;
  }): Promise<StoryboardSyncWriteResult>;
  /** Write the text files, each atomically. */
  writeText(request: {
    rootDir: string;
    boardFolder: string;
    files: readonly { file: string; text: string }[];
  }): Promise<StoryboardSyncWriteResult>;
}

export interface StoryboardSyncPrepareResult {
  error?: string;
  /** The board folder's absolute path. */
  dir?: string;
  /** assetId → relative file path, for every file already in `assets/`. */
  assets?: Record<string, string>;
}

export interface StoryboardSyncWriteResult {
  error?: string;
  /** The board folder's absolute path. */
  dir?: string;
}

let host: KolaHost | null = null;

/** Install (or clear, with null) the host. Idempotent; call before the first render. */
export function installHost(next: KolaHost | null): void {
  host = next;
}

export function getHost(): KolaHost | null {
  return host;
}

/** Tell the host a board's metadata moved (core/api.ts calls it after each such write). */
export function announceBoardsChanged(): void {
  host?.boardsChanged?.announce();
}

/**
 * Run `listener` when the host hears that a board moved elsewhere — refetch
 * what you showed from REST. A no-op in the web shell.
 */
export function onBoardsChanged(listener: () => void): () => void {
  return host?.boardsChanged?.subscribe(listener) ?? (() => {});
}

/** F-152: can this shell open the host's Review page to release a Short? */
export function canOpenReleaseInReview(): boolean {
  return typeof host?.openReleaseInReview === "function";
}

/** F-152: open the host's Review page for a Short's release — a no-op without one. */
export function openReleaseInReview(boardId: string, slot: number): void {
  host?.openReleaseInReview?.(boardId, slot);
}

/**
 * Tell the host `key` holds unsaved work (beside the page's own
 * `beforeunload` refusal); call the returned function once it no longer
 * does. Bound to the host installed NOW, so the release still reaches it
 * when the shell unmounts first. A no-op in the web shell.
 */
export function holdUnsavedWork(key: string): () => void {
  const holder = host;
  holder?.setUnsavedWork?.(key, true);
  return () => holder?.setUnsavedWork?.(key, false);
}

/** The host's own dashboard views — none in the web shell. */
export function hostDashboardViews(): readonly HostDashboardView[] {
  return host?.dashboardViews ?? [];
}

/** F-155: the host's video placer for a Short's script — null in the web shell. */
export function hostVideoPlacer(): ((scriptPath: string) => Promise<HostPlaceVideoResult>) | null {
  const source = host?.fileSource;
  return typeof source?.placeVideo === "function" ? (scriptPath) => source.placeVideo!(scriptPath) : null;
}

/** The host's own folders for *Fill from a file* — none in the web shell. */
export function hostFileSource(): HostFileSource | null {
  return host?.fileSource ?? null;
}

/** Absolute (embedded) or relative (web) URL for a server path starting with "/". */
export function apiUrl(path: string): string {
  return host === null ? path : `${host.apiBase}${path}`;
}

/** WebSocket URL for a server path: derived from `apiBase` when embedded, from the page otherwise. */
export function wsUrl(path: string): string {
  if (host === null) {
    const proto = window.location.protocol === "https:" ? "wss" : "ws";
    return `${proto}://${window.location.host}${path}`;
  }
  // product: a same-origin host names a path prefix ("/kola") rather than an origin.
  if (host.apiBase.startsWith("/")) {
    const proto = window.location.protocol === "https:" ? "wss" : "ws";
    return `${proto}://${window.location.host}${host.apiBase}${path}`;
  }
  const base = new URL(host.apiBase);
  const proto = base.protocol === "https:" ? "wss" : "ws";
  return `${proto}://${base.host}${path}`;
}

/**
 * Hand an http(s) URL to the user's browser. `window.open(_blank)` is the web
 * shell's whole mechanism (docs/02 F-31: a plain browser opens a tab; the
 * Flutter shell turns the off-origin navigation into an external launch);
 * the desktop app's windows DENY every `window.open`, so the embedded shell
 * asks the host instead, which opens the OS default browser.
 */
export function openExternalUrl(url: string): void {
  if (host !== null) {
    host.openExternal(url);
    return;
  }
  window.open(url, "_blank", "noopener,noreferrer");
}

/** The page title — a no-op when embedded, where the window belongs to the host. */
export function setDocumentTitle(title: string): void {
  if (host === null) document.title = title;
}
